import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Range } from 'rc-slider';
import 'rc-slider/assets/index.css';
import { cloneDeep, isNull, isNaN, isUndefined } from 'lodash';
import { Button, ButtonGroup } from 'react-bootstrap';
import {
  Condition,
  NO_SELECTED_MAKES,
} from '@upstart/harmony/lib/legos/constants';
import ProdigyInput from '../../GlobalUI/ProdigyInput';
import { BRANDS } from '../../../util/constants';
import feedviewsActions from '../../../redux/feedviews/actions';
import FeedviewFilterPreview from './FeedviewFilterPreview';
import '../Feedview.scss';

function FeedviewFilters(props) {
  const { feedview, actions, history } = props;
  const [previewMode, setPreviewMode] = useState(false);
  const [previewWhere, setPreviewWhere] = useState(feedview.where);
  const minPrice = 0;
  const maxPrice = 1000000;

  const toObject = (arr, key) => {
    const out = {};

    arr.forEach((m) => {
      out[m] = {
        name: m,
        selected: feedview?.where?.[key]?.indexOf(m) >= 0,
      };
    });
    return out;
  };

  const [conditionMakes, setConditionMakes] = useState({
    [Condition.NEW]: toObject(BRANDS, 'makes'),
    [Condition.USED]: toObject(BRANDS, 'makes'),
    [Condition.CPO]: toObject(BRANDS, 'makes'),
  });

  const [price, setPrice] = useState([minPrice, maxPrice]);

  const repopulateUI = () => {
    const tmpMakes = cloneDeep(conditionMakes);

    // eslint-disable-next-line guard-for-in
    for (const i in feedview?.where) {
      const condition = feedview.where[i].condition[0];

      if (conditionMakes && feedview?.where[i]?.make) {
        feedview.where[i].make.forEach((make) => {
          if (!isUndefined(tmpMakes[condition][make])) {
            tmpMakes[condition][make].selected = true;
          }
        });

        if (feedview.where[i].make.length === 0) {
          // eslint-disable-next-line guard-for-in
          for (const make in conditionMakes[condition]) {
            tmpMakes[condition][make].selected = true;
          }
        }
      }

      if (conditionMakes && feedview.where[i]?.cachedOnlinePrice) {
        const tmpPrice = cloneDeep(price);
        tmpPrice[0] = feedview?.where[i]?.cachedOnlinePrice[0];
        tmpPrice[1] = feedview?.where[i]?.cachedOnlinePrice[1];
        setPrice(tmpPrice);
      }
    }

    setConditionMakes(tmpMakes);
  };

  useEffect(() => {
    if (isNull(props.match.params.feedviewId)) {
      // Feedview is Invalid
      // Let's redirect to create mode
      history.push('/feedview');
    }

    repopulateUI();
  }, [feedview]);

  const onSliderChange = (val) => {
    setPrice(val);
  };

  const isValidNumber = (val) => /^\d+$/.test(val);

  const changeMin = (val) => {
    const int = isNaN(parseInt(val, 10)) ? '' : parseInt(val, 10);
    const upperBound = price[1] || maxPrice;
    if (int >= upperBound || int < minPrice) {
      return;
    }
    if (isValidNumber(int) || int === '') {
      setPrice([int, price[1]]);
    }
  };

  const changeMax = (val) => {
    const int = isNaN(parseInt(val, 10)) ? '' : parseInt(val, 10);
    const lowerBound = price[0] || minPrice;
    if (int <= lowerBound || int > maxPrice) {
      return;
    }
    if (isValidNumber(int) || int === '') {
      setPrice([price[0], int]);
    }
  };

  const setAllSelectedConditionMakes = (condition, isSelected) => {
    const selectedConditionMakes = cloneDeep(conditionMakes);

    // eslint-disable-next-line guard-for-in
    for (const make in conditionMakes[condition]) {
      selectedConditionMakes[condition][make].selected = isSelected;
    }

    setConditionMakes(selectedConditionMakes);
  };

  const renderCheckbox = (condition, make) => (
    <div className={make.name}>
      <div
        className="checkbox-wrapper"
        onClick={() => {
          const tempConditionMakes = cloneDeep(conditionMakes);

          tempConditionMakes[condition][make.name].selected =
            !tempConditionMakes[condition][make.name].selected;
          setConditionMakes(tempConditionMakes);
        }}
      >
        <input
          type="checkbox"
          checked={conditionMakes[condition][make.name]?.selected || false}
          onChange={() => {}}
        />{' '}
        <div>{conditionMakes[condition][make.name]?.name}</div>
      </div>
    </div>
  );

  const renderSelectAll = (condition) => (
    <Button
      variant="secondary"
      onClick={() => setAllSelectedConditionMakes(condition, true)}
    >
      Select All
    </Button>
  );

  const renderSelectNone = (condition) => (
    <Button
      variant="secondary"
      onClick={() => setAllSelectedConditionMakes(condition, false)}
    >
      Select None
    </Button>
  );

  const renderNode = (condition, make, idx) => (
    <div key={idx}>{renderCheckbox(condition, make)}</div>
  );

  const renderConditionMakeColumn = (condition) => (
    <div
      className="col-sm-3 full-height-container filter-column"
      key={condition}
    >
      <h3>{condition} Makes</h3>
      <ButtonGroup className="pb-10">
        {renderSelectAll(condition)}
        {renderSelectNone(condition)}
      </ButtonGroup>
      <div className="makes-list-container">
        {Object.values(conditionMakes[condition]).map((make, idx) =>
          renderNode(condition, make, idx),
        )}
      </div>
    </div>
  );

  const renderFilterInputs = () => (
    <div className="row main-content-area">
      {Object.values(Condition).map((val) => renderConditionMakeColumn(val))}
      <div className="col-sm-3">
        <h3>Price</h3>
        <div className="price-range-input-container">
          <ProdigyInput
            value={price[0]}
            label="Min Price"
            onChangeHandler={changeMin}
            placeholder="0"
          />
          <ProdigyInput
            value={price[1]}
            label="Max Price"
            onChangeHandler={changeMax}
            placeholder={String(maxPrice)}
          />
        </div>
        <div className="price-range-slider-container">
          <Range
            defaultValue={price}
            value={price}
            min={0}
            max={maxPrice}
            allowCross={false}
            onChange={onSliderChange}
            step={500}
          />
        </div>
      </div>
    </div>
  );

  const buildTmpWhere = () => {
    const tmpWhere = cloneDeep(feedview.where || []);

    let i = 0;
    // eslint-disable-next-line guard-for-in
    for (const condition in conditionMakes) {
      const selectedMakes = Object.keys(conditionMakes[condition]).filter(
        (m) => conditionMakes[condition][m].selected,
      );

      if (!tmpWhere[i]) tmpWhere[i] = {};

      tmpWhere[i].make = selectedMakes;

      // reassign make if all makes selected or if none are selected, insert  NO_SELECTED_MAKES as the only value
      if (selectedMakes.length === BRANDS.length) {
        tmpWhere[i].make = [];
      } else if (selectedMakes.length === 0) {
        tmpWhere[i].make = [NO_SELECTED_MAKES];
      }

      tmpWhere[i].condition = [condition];
      tmpWhere[i].cachedOnlinePrice = price;

      // eslint-disable-next-line no-plusplus
      i++;
    }

    return tmpWhere;
  };

  const cancel = () => {
    history.push('/feedviews/');
  };

  const preview = () => {
    const tmpWhere = buildTmpWhere();
    setPreviewWhere(tmpWhere);
    setPreviewMode(!previewMode);
  };

  const submitForm = () => {
    const tmpWhere = buildTmpWhere();
    feedview.where = tmpWhere;
    actions.modifyFeedviewRequest({ feedview });
  };

  return (
    <div className="feedview-tab-container">
      {!previewMode && renderFilterInputs()}
      {previewMode && (
        <FeedviewFilterPreview
          feedview={feedview}
          previewWhere={previewWhere}
        />
      )}
      <div className="row submit-button-row">
        <div>
          <Button variant="secondary" type="button" onClick={() => cancel()}>
            Cancel
          </Button>
          {previewMode && (
            <Button variant="primary" type="button" onClick={() => preview()}>
              Back To Filters
            </Button>
          )}
        </div>
        <div>
          {!previewMode && (
            <Button variant="primary" type="button" onClick={() => preview()}>
              Preview
            </Button>
          )}
          <Button variant="primary" type="button" onClick={() => submitForm()}>
            Save Filters
          </Button>
        </div>
      </div>
    </div>
  );
}

FeedviewFilters.propTypes = {
  feedview: PropTypes.object,
  actions: PropTypes.object,
  history: PropTypes.object,
  match: PropTypes.object,
};

const mapStateToProps = ({ feeds }) => ({ feeds });

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      ...feedviewsActions,
    },
    dispatch,
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(FeedviewFilters);
